import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiUserResponse } from '../models/api.models';
import {
  ActivateAccountPayload,
  ChangePasswordPayload,
  ForgotPasswordPayload,
  LoginPayload,
  RegisterPayload,
  ResetPasswordPayload,
  UpdateNotificationPreferencesPayload,
  UpdateProfilePayload,
  User,
} from '../models/auth.models';
import { AuthService } from './auth.service';
import { OrganizationContextService } from './organization-context.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthStoreService {
  private readonly authService = inject(AuthService);
  private readonly storage = inject(StorageService);
  private readonly organizationContext = inject(OrganizationContextService);
  private readonly router = inject(Router);

  private readonly userSignal = signal<User | null>(null);
  private readonly tokenSignal = signal<string | null>(this.storage.getAuthToken());
  private readonly loadingSignal = signal(false);
  private readonly initializedSignal = signal(false);

  readonly user = this.userSignal.asReadonly();
  readonly token = this.tokenSignal.asReadonly();
  readonly isLoading = this.loadingSignal.asReadonly();
  readonly isInitialized = this.initializedSignal.asReadonly();
  readonly isAuthenticated = computed(() => Boolean(this.userSignal() && this.tokenSignal()));
  readonly isAdmin = computed(() => {
    const roles = this.userSignal()?.type ?? [];
    return roles.some((role) => ['ADMIN', 'MANAGER', 'SUPERADMIN'].includes(role));
  });

  getDefaultAuthenticatedRoute(): string {
    return this.isAdmin() ? '/admin' : '/perfil';
  }

  async initialize(): Promise<void> {
    if (this.initializedSignal()) {
      return;
    }

    const token = this.storage.getAuthToken();

    if (!token) {
      this.initializedSignal.set(true);
      return;
    }

    this.tokenSignal.set(token);
    this.loadingSignal.set(true);

    try {
      const response = await firstValueFrom(this.authService.me());
      this.userSignal.set(response.user);
    } catch {
      this.clearSession(false);
    } finally {
      this.loadingSignal.set(false);
      this.initializedSignal.set(true);
    }
  }

  async login(payload: LoginPayload): Promise<User> {
    this.loadingSignal.set(true);

    try {
      const response = await firstValueFrom(this.authService.login(payload));
      this.persistTokens(response);
      this.userSignal.set(response.user);
      return response.user;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  async register(payload: RegisterPayload): Promise<User> {
    this.loadingSignal.set(true);

    try {
      const response = await firstValueFrom(this.authService.register(payload));
      this.persistTokens(response);
      this.userSignal.set(response.user);
      return response.user;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  async logout(redirect = true): Promise<void> {
    const refreshToken = this.storage.getRefreshToken();

    try {
      if (refreshToken) {
        await firstValueFrom(this.authService.logout({ refresh_token: refreshToken }));
      }
    } catch {
      // Ignore logout API errors and clear local session anyway.
    } finally {
      this.clearSession(redirect);
    }
  }

  /**
   * Exchanges the stored refresh token for a new access/refresh token pair.
   * Used by the auth interceptor to transparently renew an expired session.
   *
   * The refresh token is single-use and rotated server-side, so concurrent tabs
   * racing to refresh with the same stored value would have all but one fail.
   * `navigator.locks` serializes this across tabs; once a tab acquires the lock it
   * re-checks storage in case another tab already refreshed while it was waiting,
   * and reuses that result instead of sending an already-invalidated refresh token.
   */
  async refresh(expiredToken?: string): Promise<string> {
    const doRefresh = async (): Promise<string> => {
      if (expiredToken) {
        const currentToken = this.storage.getAuthToken();
        if (currentToken && currentToken !== expiredToken) {
          this.tokenSignal.set(currentToken);
          return currentToken;
        }
      }

      const refreshToken = this.storage.getRefreshToken();

      if (!refreshToken) {
        throw new Error('No refresh token available.');
      }

      const response = await firstValueFrom(this.authService.refresh({ refresh_token: refreshToken }));
      this.persistTokens(response);
      this.userSignal.set(response.user);

      return response.token!;
    };

    if (typeof navigator === 'undefined' || !navigator.locks) {
      return doRefresh();
    }

    return navigator.locks.request('auth-token-refresh', doRefresh);
  }

  private persistTokens(response: ApiUserResponse<User>): void {
    if (response.token) {
      this.storage.setAuthToken(response.token);
      this.tokenSignal.set(response.token);
    }
    if (response.refresh_token) {
      this.storage.setRefreshToken(response.refresh_token);
    }
  }

  async forgotPassword(payload: ForgotPasswordPayload): Promise<string> {
    const response = await firstValueFrom(this.authService.forgotPassword(payload));
    return response.message;
  }

  async resetPassword(payload: ResetPasswordPayload): Promise<string> {
    const response = await firstValueFrom(this.authService.resetPassword(payload));
    return response.message;
  }

  async activateAccount(
    id: string,
    hash: string,
    payload: ActivateAccountPayload,
    params: Record<string, unknown>,
  ): Promise<string> {
    const response = await firstValueFrom(this.authService.activateAccount(id, hash, payload, params));
    return response.message;
  }

  async updateProfile(payload: UpdateProfilePayload): Promise<User> {
    const response = await firstValueFrom(this.authService.updateProfile(payload));
    this.userSignal.set(response.user);
    return response.user;
  }

  async changePassword(payload: ChangePasswordPayload): Promise<string> {
    const response = await firstValueFrom(this.authService.changePassword(payload));
    return response.message;
  }

  async updateNotificationPreferences(payload: UpdateNotificationPreferencesPayload): Promise<User> {
    const response = await firstValueFrom(this.authService.updateNotificationPreferences(payload));
    this.userSignal.set(response.user);
    return response.user;
  }

  async uploadProfileImage(file: File): Promise<string | null | undefined> {
    const response = await firstValueFrom(this.authService.uploadProfileImage(file));
    this.userSignal.set(response.user);
    return response.user.image_url;
  }

  clearSession(redirect = true): void {
    this.storage.clearAuthToken();
    this.storage.clearRefreshToken();
    this.tokenSignal.set(null);
    this.userSignal.set(null);
    this.organizationContext.clear();

    if (redirect) {
      this.router.navigate(['/login']);
    }
  }
}
